import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, BookOpen, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCourse } from '../context/CourseContext';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import ContentViewer from '../components/ContentViewer';
import QuizSection from '../components/QuizSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SectionReader() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [section, setSection] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [currentSubtopic, setCurrentSubtopic] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);
  const { markSubtopicComplete, setTotalSubtopics } = useCourse();

  const numId = parseInt(sectionId);

  // Load course metadata
  useEffect(() => {
    axios.get(`${API}/course`).then(res => {
      setCourse(res.data);
      setTotalSubtopics(res.data.total_subtopics);
    }).catch(err => console.error(err));
  }, [setTotalSubtopics]);

  // Load section content and quiz
  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/course/sections/${numId}`),
      axios.get(`${API}/course/quizzes/${numId}`)
    ]).then(([secRes, quizRes]) => {
      setSection(secRes.data);
      setQuiz(quizRes.data);
      if (secRes.data.subtopics?.length > 0) {
        setCurrentSubtopic(secRes.data.subtopics[0].id);
      }
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [numId]);

  const scrollToTop = useCallback(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubtopicClick = useCallback((id) => {
    setCurrentSubtopic(id);
    setSidebarOpen(false);
    scrollToTop();
  }, [scrollToTop]);

  const handleComplete = useCallback((subtopicId) => {
    markSubtopicComplete(subtopicId);
    // Auto-advance to next
    if (section?.subtopics) {
      const idx = section.subtopics.findIndex(st => st.id === subtopicId);
      if (idx < section.subtopics.length - 1) {
        setTimeout(() => {
          setCurrentSubtopic(section.subtopics[idx + 1].id);
          scrollToTop();
        }, 500);
      } else {
        setTimeout(() => {
          setCurrentSubtopic('quiz');
          scrollToTop();
        }, 500);
      }
    }
  }, [section, markSubtopicComplete, scrollToTop]);

  const handleNext = useCallback(() => {
    if (!section?.subtopics) return;
    if (currentSubtopic === 'quiz') return;
    const idx = section.subtopics.findIndex(st => st.id === currentSubtopic);
    if (idx < section.subtopics.length - 1) {
      setCurrentSubtopic(section.subtopics[idx + 1].id);
    } else {
      setCurrentSubtopic('quiz');
    }
    scrollToTop();
  }, [section, currentSubtopic, scrollToTop]);

  const currentSubtopicData = section?.subtopics?.find(st => st.id === currentSubtopic);
  const currentSubtopicIdx = section?.subtopics?.findIndex(st => st.id === currentSubtopic) ?? -1;
  const isLastSubtopic = currentSubtopicIdx === (section?.subtopics?.length ?? 0) - 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-10 h-10 rounded-lg bg-sf-blue flex items-center justify-center mx-auto mb-3 animate-pulse">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <p className="text-sm text-muted-foreground">Loading section...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="section-reader">
      <Header onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} courseSections={course?.sections} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb navigation */}
        <div className="flex items-center gap-2 mb-6 text-sm" data-testid="section-breadcrumb">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/home')}
            className="text-muted-foreground hover:text-foreground -ml-2"
            data-testid="back-to-dashboard-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            All Sections
          </Button>
          <span className="text-border">/</span>
          <span className="text-foreground font-medium truncate">
            Section {numId}: {section?.title}
          </span>
          {section?.reading_time && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full ml-2">
              <Clock className="h-3 w-3" /> {section.reading_time} min read
            </span>
          )}
        </div>

        {/* Section image */}
        {section?.image && (
          <div className="mb-8 rounded-xl overflow-hidden border border-border h-48 sm:h-56" data-testid="section-banner">
            <img src={section.image} alt={section.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar - mobile overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/30 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div className={`
            ${sidebarOpen ? 'fixed inset-y-0 left-0 z-50 w-80 pt-16' : 'hidden'}
            lg:block lg:relative lg:w-auto lg:pt-0 lg:z-auto
            col-span-1 lg:col-span-3
          `}>
            {course && (
              <Sidebar
                sections={course.sections}
                currentSectionId={numId}
                currentSubtopicId={currentSubtopic}
                onSubtopicClick={handleSubtopicClick}
                className="lg:sticky lg:top-24"
              />
            )}
          </div>

          {/* Main Content */}
          <div className="col-span-1 lg:col-span-9" ref={contentRef}>
            {currentSubtopic === 'quiz' ? (
              <QuizSection
                key={numId}
                sectionId={numId}
                questions={quiz?.questions}
                sectionTitle={section?.title}
              />
            ) : currentSubtopicData ? (
              <ContentViewer
                subtopic={currentSubtopicData}
                onComplete={handleComplete}
                onNext={handleNext}
                isLast={isLastSubtopic}
              />
            ) : (
              <p className="text-muted-foreground">Select a topic from the sidebar.</p>
            )}

            {/* Section navigation */}
            <div className="mt-12 pt-8 border-t border-border flex items-center justify-between" data-testid="section-nav">
              {numId > 1 && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/section/${numId - 1}`)}
                  data-testid="prev-section-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous Section
                </Button>
              )}
              <div className="flex-1" />
              {course && numId < course.total_sections && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/section/${numId + 1}`)}
                  data-testid="next-section-btn"
                >
                  Next Section
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
